import type { ChartResult } from '@astrally/shared';

/**
 * Human-readable summary of what changed between two chart computations —
 * shown after a birth-time refinement or correction so users see exactly
 * which results moved.
 */
export function diffCharts(before: ChartResult, after: ChartResult): string[] {
  const changes: string[] = [];

  if (before.meta.timeConfidence !== after.meta.timeConfidence) {
    changes.push(
      `Birth-time status: ${before.meta.timeConfidence} → ${after.meta.timeConfidence}`,
    );
  }

  const lagnaA = before.jyotish?.lagna?.signName;
  const lagnaB = after.jyotish?.lagna?.signName;
  if (lagnaA !== lagnaB) {
    changes.push(`Lagna: ${lagnaA ?? 'not calculated'} → ${lagnaB ?? 'not calculated'}`);
  }

  const byPlanet = (c: ChartResult) =>
    new Map((c.positions ?? []).map((p) => [p.planet, p] as const));
  const a = byPlanet(before);
  const b = byPlanet(after);
  for (const [planet, posB] of b) {
    const posA = a.get(planet);
    if (!posA) continue;
    if (posA.sidereal.sign !== posB.sidereal.sign) {
      changes.push(`${planet}: rashi ${posA.sidereal.signName} → ${posB.sidereal.signName}`);
    } else if (posA.nakshatra.index !== posB.nakshatra.index) {
      changes.push(`${planet}: nakshatra ${posA.nakshatra.name} → ${posB.nakshatra.name}`);
    }
  }

  const dashaA = before.jyotish?.vimshottari?.moonNakshatra.lord;
  const dashaB = after.jyotish?.vimshottari?.moonNakshatra.lord;
  if (dashaA && dashaB && dashaA !== dashaB) {
    changes.push(`Birth mahadasha lord: ${dashaA} → ${dashaB}`);
  }

  const housesA = before.western?.houses ? 'available' : 'not available';
  const housesB = after.western?.houses ? 'available' : 'not available';
  if (housesA !== housesB) changes.push(`Houses: ${housesA} → ${housesB}`);

  if (changes.length === 0) {
    changes.push('No sign-level changes — degrees and timing precision were refined.');
  }
  return changes;
}
